/* global FormData */

import axios from 'axios'
import { CANCEL } from 'redux-saga'

import { APPLICATION, SEARCH, INTEGRATION } from '../config'
import { formatCompanySearchQuery, formatJobSearchQuery } from '../utils/apiConverter'
import { appendQueryParams } from '../utils/url'

export const API_PREFIX = process.env.API_PREFIX || ''

export const SEARCH_JOBS = `${API_PREFIX}/yapi/jobs/`
export const JOB_DETAIL = (jobId) => {
  const query = {}
  if (APPLICATION.preferMailto) {
    query.prefer_mailto = '1'
  }
  if (SEARCH.forUkraine) {
    query.for_ukraine = '1'
  }
  return appendQueryParams(`${API_PREFIX}/yapi/jobs/${jobId}/`, query)
}
export const SIMILAR_JOBS = (jobId) => `${API_PREFIX}/yapi/jobs/${jobId}/similar/`

export const SEARCH_COMPANIES = `${API_PREFIX}/yapi/companies/`
export const SEARCH_COMPANY_JOBS = `${API_PREFIX}/yapi/companies`
export const AUTOCOMPLETE_SUGGESTIONS = `${API_PREFIX}/suche/ajax/autocomplete`
export const COMPANY_DETAIL = `${API_PREFIX}/yapi/companies`
export const JOBMAIL_SUBSCRIBE = `${API_PREFIX}/suche/jobmail/add/`
export const JPM_UNSUBSCRIBE = `${API_PREFIX}/account/jpm/deactivate/`
export const COMPANY_JOBMAIL_SUBSCRIBE = `${API_PREFIX}/yapi/company_jobmails/subscriptions/`
export const JOBMAIL_SUBSCRIBE_AUTH = `${API_PREFIX}/suche/jobmail/add/authenticated/`
export const JOBMAIL_RESEND_ACTIVATION_EMAIL = `${API_PREFIX}/account/jobmail/activate/resend/`
export const JOBMAIL_UNSUBSCRIBE = `${API_PREFIX}/account/jobmail/deactivate/`
export const SIGN_S3_UPLOAD = `${API_PREFIX}/sign_s3_upload`
export const AUTH_LOGIN = `${API_PREFIX}/account/ajax/signin/`
export const AUTH_LOGOUT = `${API_PREFIX}/account/ajax/signout/`
export const AUTH_REGISTER = `${API_PREFIX}/account/ajax/signup/`
export const AUTH_RESET = `${API_PREFIX}/account/ajax/password/reset/`
export const ATTACHMENTS_INFO = `${API_PREFIX}/attachments/info/`
export const COMPANY_JOBMAIL_UNSUBSCRIBE = (companyJobmailId) => `${API_PREFIX}/yapi/company_jobmails/subscriptions/${companyJobmailId}/delete/`
export const GOOGLE_MAPS_GEOCODE = 'https://maps.google.com/maps/api/geocode/json'

/**
 * Generates URL to accept internal event information
 * @param {string} eventName - Name of the event that is tracked
 * @param {string} jobId - Id of the job for which event is tracked
 * @param {object} params - Parameters used to generate request URL (currently only "backfill" param supported)
 * @returns {String}
 */
export const INTERNAL_EVENT_JOB = (eventName, jobId, { fromSta, fromKalaydo, fromGeneric, isEntry, trackUrl } = {}) => {
  const query = {}
  if (fromSta) {
    query.from_sta = '1'
  }
  if (fromKalaydo) {
    query.from_kalaydo = '1'
  }
  if (fromGeneric) {
    query.from_gen = '1'
  }
  if (isEntry !== undefined) {
    query.isEntry = isEntry ? '1' : '0'
  }
  if (trackUrl !== undefined) {
    query.currentUrl = trackUrl
  }
  return appendQueryParams(`${API_PREFIX}/bothunt/${eventName}/${jobId}/`, query)
}

export const INTERNAL_EVENT_COMPANY = (eventName, companyId) => `${API_PREFIX}/bothunt/stand.Stand.${eventName}.${companyId}/`

export const DEFAULT_CONFIG = {
  withCredentials: process.env.API_CREDENTIALS || false
}

axios.defaults.xsrfHeaderName = 'X-CSRFToken'
axios.defaults.xsrfCookieName = 'csrftoken'

export function prepareData (data, Type = URLSearchParams) {
  if (data instanceof Type) return data
  const q = new Type()
  if (!data) return q

  const append = (key, value, { expandArrays = true } = {}) => {
    if (typeof value === 'undefined') return
    if (expandArrays && Array.isArray(value)) {
      value.forEach(subvalue => append(key, subvalue, { expandArrays: false }))
      return
    }
    q.append(key, value)
  }

  if (Symbol.iterator in data) {
    for (const entry of data) {
      // we are expecting entries like [key, value]
      append(entry[0], entry[1])
    }
  } else {
    for (const key in data) {
      if (!Object.prototype.hasOwnProperty.call(data, key)) continue
      append(key, data[key])
    }
  }
  return q
}

export function request (config = {}) {
  const source = axios.CancelToken.source()
  const request = axios({
    ...DEFAULT_CONFIG,
    ...config,
    cancelToken: source.token
  })
  request[CANCEL] = (reason = 'Operation canceled by saga CANCEL') => source.cancel()
  request._source = source
  request.cancel = (reason = 'Operation canceled by cancel()') => source.cancel()
  return request
}

export default {
  search: {
    job (query, opts) {
      return request({
        ...opts,
        method: 'POST',
        url: SEARCH_JOBS,
        data: prepareData(formatJobSearchQuery(query), URLSearchParams),
        transformResponse: [unparsed => {
          const {
            orderOptions,
            result: data,
            resultParams,
            resultSize,
            resultSizeHighQuality,
            resultSizeLowQuality,
            spellcheck
          } = JSON.parse(unparsed)
          return {
            data,
            orderOptions,
            resultParams,
            resultSize,
            resultSizeHighQuality,
            resultSizeLowQuality,
            spellcheck
          }
        }]
      })
    },
    company (query, opts) {
      return request({
        ...opts,
        method: 'GET',
        url: SEARCH_COMPANIES,
        params: prepareData(formatCompanySearchQuery(query), URLSearchParams),
        transformResponse: [unparsed => {
          const {
            orderOptions,
            result: data,
            resultParams,
            resultSize,
            resultSizeHighQuality,
            resultSizeLowQuality,
            spellcheck
          } = JSON.parse(unparsed)
          return {
            data,
            orderOptions,
            resultParams,
            resultSize,
            resultSizeHighQuality,
            resultSizeLowQuality,
            spellcheck
          }
        }]
      })
    },
    companyJobs ({ companyId, page, filter, ...query }, opts) {
      return request({
        ...opts,
        method: 'GET',
        url: `${SEARCH_COMPANY_JOBS}/${companyId}/jobs/`,
        params: prepareData(formatJobSearchQuery({
          cur_page: page,
          Vertragsart: filter,
          ...query
        }), URLSearchParams)
      })
    },
    similarJobs (id, params) {
      return request({
        method: 'GET',
        url: SIMILAR_JOBS(id),
        params: prepareData(params, URLSearchParams),
        transformResponse: [unparsed => {
          const { result, resultSize } = JSON.parse(unparsed)
          return { result, resultSize }
        }]
      })
    }
  },
  getAutocompleteSuggestions (params, opts) {
    return request({
      ...opts,
      method: 'GET',
      url: AUTOCOMPLETE_SUGGESTIONS,
      params
    })
  },
  getJobDetail (id, { ...opts } = {}) {
    return request({
      ...opts,
      method: 'GET',
      url: JOB_DETAIL(id),
      transformResponse: [unparsed => {
        const { data, ...response } = JSON.parse(unparsed)
        return ({
          ...response,
          data: {
            id,
            ...data
          }
        })
      }]
    })
  },
  getCompanyDetail (id, opts) {
    return request({
      ...opts,
      method: 'GET',
      url: `${COMPANY_DETAIL}/${id}/`
    })
  },
  subscribeJobmail (data, opts) {
    return request({
      ...opts,
      method: 'POST',
      url: JOBMAIL_SUBSCRIBE,
      data
    })
  },
  subscribeCompanyJobmail (data, opts) {
    return request({
      ...opts,
      method: 'POST',
      url: COMPANY_JOBMAIL_SUBSCRIBE,
      data
    })
  },
  subscribeJobmailAuth (data, opts) {
    return request({
      ...opts,
      method: 'POST',
      url: JOBMAIL_SUBSCRIBE_AUTH,
      data
    })
  },
  unsubscribeJobmail (data, opts) {
    return request({
      ...opts,
      method: 'POST',
      url: JOBMAIL_UNSUBSCRIBE,
      data
    })
  },
  unsubscribeJpm (data, opts) {
    return request({
      ...opts,
      method: 'POST',
      url: JPM_UNSUBSCRIBE,
      data
    })
  },
  unsubscribeCompanyJobmail (companyJobmailId, data, opts) {
    return request({
      ...opts,
      method: 'POST',
      url: COMPANY_JOBMAIL_UNSUBSCRIBE(companyJobmailId),
      data
    })
  },
  resendJobmailActivationEmail (data, opts) {
    return request({
      ...opts,
      method: 'POST',
      url: JOBMAIL_RESEND_ACTIVATION_EMAIL,
      data
    })
  },
  /**
   * Get signed url for uploading file to Amazon S3
   * @param {File} params
   * @param {object} opts
   * @returns {AxiosPromise}
   */
  signS3Upload ({ name, type }, opts) {
    return request({
      ...opts,
      method: 'GET',
      url: SIGN_S3_UPLOAD,
      params: { file_name: name, file_type: type }
    })
  },
  /**
   * Upload file to Amazon S3 once a signed url was received
   * @param {File} file
   * @param {String} url
   * @param {Object} fields
   * @param {Object} opts
   * @returns {AxiosPromise}
   */
  uploadS3 (file, { url, fields }, opts) {
    return request({
      ...opts,
      method: 'POST',
      url,
      data: prepareData({
        ...fields,
        file
      }, FormData)
    })
  },
  getAttachments () {
    return request({
      method: 'GET',
      url: ATTACHMENTS_INFO
    })
  },
  postAttachmentsInfo (data) {
    return request({
      method: 'POST',
      url: ATTACHMENTS_INFO,
      data: prepareData(data, URLSearchParams)
    })
  },
  submitDetailedJobApplication (applicationUrl, data, opts) {
    return request({
      ...opts,
      method: 'POST',
      url: `${API_PREFIX}${applicationUrl}`,
      data: prepareData(data, URLSearchParams)
    })
  },
  submitExpressJobApplication (applicationUrl, { read_data_privacy_statement: dataPrivacy, ...data }, opts) {
    return request({
      ...opts,
      method: 'POST',
      url: `${API_PREFIX}${applicationUrl}`,
      data: prepareData({ privacy_agreement: dataPrivacy, ...data }, URLSearchParams)
    })
  },
  /**
   * Get similar job info for application to be sent
   * @param {*} jobId - Original job id
   * @param {*} params - Additional request params: with_backfill, for_ukraine, exclude_ids
   * @returns {AxiosPromise}
   */
  requestJobForApplication (jobId, params) {
    return request({
      method: 'GET',
      url: `${API_PREFIX}/yapi/jobs/${jobId}/similar/for_application`,
      params: prepareData(params, URLSearchParams)
    })
  },
  /**
   * <b>Important</b>: use trackingInternalEvent from tracking/utils/trackingInternalEvent
   * @deprecated
   * @param {string} eventName - Name of the event that is tracked
   * @param {string} jobId - Id of the job for which event is tracked
   * @param {object} params - Parameters used to generate request URL
   * @return {Promise<void>} - cancelable promise
   */
  trackInternalJobEvent (eventName, jobId, params) {
    return request({
      method: 'POST',
      url: INTERNAL_EVENT_JOB(eventName, jobId, params)
    })
  },
  /**
   * <b>Important</b>: use trackInternalEvent from tracking/utils/trackingInternalEvent
   * @deprecated
   * @param {string} eventName - Name of the event that is tracked
   * @param {number} companyId - Id of the company for which event is tracked
   * @return {Promise<void>} - cancelable promise
   */
  trackInternalCompanyEvent (eventName, companyId) {
    return request({
      method: 'POST',
      url: INTERNAL_EVENT_COMPANY(eventName, companyId)
    })
  },
  auth: {
    login ({ email = '', password = '', remember_me: rememberMe = false, ...props }, opts) {
      return request({
        ...opts,
        method: 'POST',
        url: AUTH_LOGIN,
        data: prepareData({
          email, password, remember_me: rememberMe, ...props
        }, FormData)
      })
    },
    logout (props, opts) {
      return request({
        ...opts,
        method: 'POST',
        url: AUTH_LOGOUT
      })
    },
    register ({ email = '', password1 = '', password2 = '', ...props }, opts) {
      return request({
        ...opts,
        method: 'POST',
        url: AUTH_REGISTER,
        data: prepareData({
          email, password1, password2, ...props
        }, FormData)
      })
    },
    reset ({ email = '', ...props }, opts) {
      return request({
        ...opts,
        method: 'POST',
        url: AUTH_RESET,
        data: prepareData({
          email, ...props
        }, FormData)
      })
    }
  },

  getRegionByGeolocation ({ latitude, longitude }, opts = {}) {
    return request({
      ...opts,
      method: 'GET',
      url: GOOGLE_MAPS_GEOCODE,
      params: { latlng: `${latitude},${longitude}`, key: INTEGRATION.geocode.apiKey }
    })
  }
}
